import { useEffect, useMemo, useRef, useState } from 'react';
import { userApi } from '../../../core/api/rtk-query/user.api';
import styled from 'styled-components';
import { UserEditModal } from '../../admin/users/UserEditModal';
import { groupApi } from '../../../core/api/rtk-query/group.api';
import { eventApi } from '../../../core/api/rtk-query/event.api';

const Wrap = styled.div`display:grid; gap:12px; padding:24px;`;
const Toolbar = styled.div`display:flex; justify-content:flex-end; gap:8px; margin-bottom:16px;`;
const LightInput = styled.input`padding:10px 12px; border-radius:10px; border:1px solid #dbe3f6; background:#fff; color:#0b1221; width:100%; box-sizing:border-box;`;
const LightSelect = styled.select`padding:10px 12px; border-radius:10px; border:1px solid #dbe3f6; background:#fff; color:#0b1221; width:100%; box-sizing:border-box;`;
const TableWrap = styled.div`border:1px solid #e5e8f0; border-radius:14px; overflow:visible; background:#fff;`;
const Table = styled.table`
  width:100%; border-collapse:separate; border-spacing:0; color:#0b1221;
  thead th{position:sticky; top:0; background:#f7f9ff; color:#465676; padding:12px; text-align:left; border-bottom:1px solid #e5e8f0; min-width: 90px; cursor:pointer;}
  thead th.active{ background:#eaf2ff !important; color:#0b68fe !important; }
  tbody td{border-bottom:1px solid #eef1f6; padding:12px}
`;
const Chip = styled.span`display:inline-flex; align-items:center; gap:6px; padding:4px 8px; border-radius:999px; font-size:12px; border:1px solid #dbe3f6; background:#f7f9ff; color:#0b1221;`;
const Status = styled.span<{ok:boolean}>`display:inline-block; padding:4px 8px; border-radius:999px; font-size:12px; border:1px solid ${p=>p.ok?'#c9e9dd':'#f5c2c7'}; background:${p=>p.ok?'#e6faf3':'#fde8ea'}; color:${p=>p.ok?'#03543f':'#9b1c1c'};`;
const Actions = styled.div`display:flex; gap:8px;`;
const Btn = styled.button`background:#fff; border:1px solid #dbe3f6; color:#0b1221; padding:8px 12px; border-radius:10px; cursor:pointer;`;
const PageBtn = styled.button`
  background:#fff; border:1px solid #dbe3f6; color:#0b1221; padding:8px 10px; border-radius:10px; min-width:36px; cursor:pointer;
  &[disabled]{opacity:.6; cursor:default}
`;
const Pagination = styled.div`display:flex; gap:6px; justify-content:flex-end; padding-top:10px;`;
const ComboWrap = styled.div`position: relative;`;
const ComboList = styled.ul`
  position: absolute; left: 0; right: 0; top: calc(100% + 6px);
  z-index: 50; list-style: none; margin: 0; padding: 8px 0; max-height: 240px; overflow: auto;
  background: #fff; border: 1px solid #dbe3f6; border-radius: 12px; box-shadow: 0 8px 28px rgba(0,0,0,.08);
`;
const ComboItem = styled.li`
  padding: 8px 12px; cursor: pointer; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;
  &:hover{background:#f7f9ff}
`;

export const TeacherStudentsPageNew = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [editUser, setEditUser] = useState<any|null>(null);
  const [groupOpen, setGroupOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(25);
  const [sortKey, setSortKey] = useState<'isuNumber'|'name'|'email'|'role'|'isuGroup'|'verified'>('name');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');

  // фильтры
  const [fIsu, setFIsu] = useState('');
  const [fName, setFName] = useState('');
  const [fEmail, setFEmail] = useState('');
  const [fGroup, setFGroup] = useState('');
  const [fEvent, setFEvent] = useState('');
  const [fVerified, setFVerified] = useState('');

  const [updateUser] = userApi.useUpdateUserMutation();
  const [verifyUser] = userApi.useVerifyUserMutation();
  
  // Получаем курсы и события преподавателя
  const { data: activeGroups } = groupApi.useGetActiveGroupQuery();
  const { data: archivedGroups } = groupApi.useGetArchivedGroupQuery();
  const allGroups = [...(activeGroups || []), ...(archivedGroups || [])];
  const { data: teacherEvents } = eventApi.useGetTeacherEventsQuery();

  // Загружаем студентов из каждого курса
  const groupMembersQueries = allGroups.map((group: any) =>
    groupApi.useGetUsersInGroupQuery({ groupId: group.id, pageNumber: 1, pageSize: 100 })
  );

  // Собираем всех уникальных студентов из всех курсов
  const allStudents = useMemo(() => {
    const studentsMap = new Map();
    
    groupMembersQueries.forEach(query => {
      const users = query.data?.users || [];
      users.forEach((user: any) => {
        if (user.role === 'Student' && !studentsMap.has(user.id)) {
          studentsMap.set(user.id, {
            id: user.id,
            name: user.name,
            email: user.email,
            isuNumber: user.isuNumber,
            isuGroup: user.isuGroup,
            verified: user.verified,
          });
        }
      });
    });
    
    return Array.from(studentsMap.values());
  }, [groupMembersQueries.map(q => q.data).join(',')]);

  const isFetching = false;
  const refetch = () => {};
  
  useEffect(() => {
    let list: any[] = [...allStudents];
    
    // Фильтр по имени
    if (fName) {
      list = list.filter((u: any) => u.name && u.name.toLowerCase().includes(fName.toLowerCase()));
    }
    
    // Фильтр по email
    if (fEmail) {
      list = list.filter((u: any) => u.email && u.email.toLowerCase().includes(fEmail.toLowerCase()));
    }
    
    // Фильтр по ISU номеру
    if (fIsu) {
      list = list.filter((u: any) => u.isuNumber && String(u.isuNumber).includes(fIsu));
    }
    
    // Фильтр по группе ISU
    if (fGroup && fGroup !== '__none__') {
      list = list.filter((u: any) => u.isuGroup && u.isuGroup.toLowerCase().includes(fGroup.toLowerCase()));
    }
    if (fGroup === '__none__') {
      list = list.filter((u: any) => !u.isuGroup || !String(u.isuGroup).trim());
    }
    
    // Фильтр по событию
    if (fEvent && fEvent !== 'all') {
      // Здесь нужно было бы проверить участие в событии, но упростим
      // В реальности нужен отдельный API для получения студентов события
    }
    
    // Фильтр по верификации
    if (fVerified !== '') {
      const isVerified = fVerified === 'true';
      list = list.filter((u: any) => !!u.verified === isVerified);
    }
    
    // Сортировка
    list = list.sort((a: any, b: any) => {
      const va = a[sortKey]; const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    
    // Пагинация на клиенте
    const start = (pageNumber - 1) * pageSize;
    const end = start + pageSize;
    setUsers(list.slice(start, end));
  }, [allStudents, sortKey, sortDir, fGroup, fEvent, fName, fEmail, fIsu, fVerified, pageNumber, pageSize]);

  const total = allStudents.length;

  const groupSuggestions = useMemo(()=>{
    const byUsers = Array.from(new Set(users.map(u=>u.isuGroup).filter(Boolean)));
    const byApi = allGroups.map((g: any) => g.name);
    return Array.from(new Set([...byApi, ...byUsers])).sort();
  }, [users, allGroups]);

  return (
    <Wrap>
      <h2 style={{margin:0, marginBottom:16}}>Мои студенты</h2>
      <Toolbar>
        <div style={{fontSize:14, color:'#667'}}>Всего: {users.length}</div>
      </Toolbar>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th className={sortKey==='isuNumber'?'active':''} onClick={() => { setSortKey('isuNumber'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }}>ИСУ</th>
              <th className={sortKey==='name'?'active':''} onClick={() => { setSortKey('name'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }}>ФИО</th>
              <th className={sortKey==='email'?'active':''} onClick={() => { setSortKey('email'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }}>Почта</th>
              <th className={sortKey==='isuGroup'?'active':''} onClick={() => { setSortKey('isuGroup'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }}>Группа ИСУ</th>
              <th>Курсы</th>
              <th className={sortKey==='verified'?'active':''} onClick={() => { setSortKey('verified'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }}>Статус</th>
              <th>Действия</th>
            </tr>
            <tr>
              <th style={{position:'relative'}}>
                <LightInput placeholder="ИСУ" value={fIsu} onChange={e=> setFIsu(e.target.value)} />
                {fIsu && (<span onClick={()=> setFIsu('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
              </th>
              <th style={{position:'relative'}}>
                <LightInput placeholder="ФИО" value={fName} onChange={e=> setFName(e.target.value)} />
                {fName && (<span onClick={()=> setFName('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
              </th>
              <th style={{position:'relative'}}>
                <LightInput placeholder="Email" value={fEmail} onChange={e=> setFEmail(e.target.value)} />
                {fEmail && (<span onClick={()=> setFEmail('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
              </th>
              <th style={{position:'relative'}}>
                <ComboWrap onBlur={()=> setGroupOpen(false)}>
                  <LightInput placeholder="Группа ISU…" value={fGroup} onChange={e=> { setFGroup(e.target.value); setGroupOpen(true); }} onFocus={()=> setGroupOpen(true)} />
                  {groupOpen && (
                    <ComboList>
                      <ComboItem onMouseDown={()=> { setFGroup('__none__'); setGroupOpen(false); }}>__none__ <span style={{opacity:.6}}>без группы</span></ComboItem>
                      {groupSuggestions.filter(n=> n.toLowerCase().includes((fGroup||'').toLowerCase())).map(n=> (
                        <ComboItem key={n} onMouseDown={()=> { setFGroup(n); setGroupOpen(false); }}>{n}</ComboItem>
                      ))}
                    </ComboList>
                  )}
                </ComboWrap>
                {fGroup && (<span onClick={()=> setFGroup('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
              </th>
              <th style={{position:'relative'}}>
                <ComboWrap onBlur={()=> setEventOpen(false)}>
                  <LightInput placeholder="Мероприятие…" value={fEvent} onChange={e=> { setFEvent(e.target.value); setEventOpen(true); }} onFocus={()=> setEventOpen(true)} />
                  {eventOpen && (
                    <ComboList>
                      <ComboItem onMouseDown={()=> { setFEvent('all'); setEventOpen(false); }}>Все мероприятия</ComboItem>
                      {(teacherEvents || []).filter((ev: any)=> ev.name.toLowerCase().includes((fEvent||'').toLowerCase())).map((ev: any)=> (
                        <ComboItem key={ev.id} onMouseDown={()=> { setFEvent(ev.id); setEventOpen(false); }}>{ev.name}</ComboItem>
                      ))}
                    </ComboList>
                  )}
                </ComboWrap>
                {fEvent && (<span onClick={()=> setFEvent('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
              </th>
              <th>
                <LightSelect value={fVerified} onChange={e=> setFVerified(e.target.value)}>
                  <option value="">—</option>
                  <option value="true">Верифицирован</option>
                  <option value="false">Не верифицирован</option>
                </LightSelect>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td onClick={()=> setEditUser(u)} style={{cursor:'pointer'}}>{u.isuNumber}</td>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>
                  <button style={{background:'transparent', border:'none', color:'#0b68fe', cursor:'pointer', padding:0}}
                          onClick={()=> setFGroup(u.isuGroup || '')}>{u.isuGroup}</button>
                </td>
                <td>
                  {u.groupIds && u.groupIds.length > 0 ? (
                    <div style={{display:'flex', gap:4, flexWrap:'wrap'}}>
                      {u.groupIds.slice(0,2).map((gid: string) => {
                        const group = allGroups.find((g: any) => g.id === gid);
                        return group ? <Chip key={gid}>{group.name}</Chip> : null;
                      })}
                      {u.groupIds.length > 2 && <Chip>+{u.groupIds.length - 2}</Chip>}
                    </div>
                  ) : '—'}
                </td>
                <td><Status ok={!!u.verified}>{u.verified?'да':'нет'}</Status></td>
                <td>
                  <Actions>
                    <Btn onClick={()=> setEditUser(u)}>Просмотр</Btn>
                  </Actions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
      <Pagination>
        {(() => {
          const pages = Math.max(1, Math.ceil(total / pageSize));
          const cur = Math.min(pageNumber, pages);
          const btn = (p:number, label?:string, disabled?:boolean) => (
            <PageBtn key={`p-${label||p}`} disabled={disabled||p===cur} onClick={()=> setPageNumber(p)}>{label||p}</PageBtn>
          );
          const elems: any[] = [];
          if (pages <= 9) {
            for(let i=1;i<=pages;i++) elems.push(btn(i));
          } else {
            elems.push(btn(1));
            if (cur>4) elems.push(btn(cur-2, '…', true));
            for(let i=Math.max(2, cur-2); i<=Math.min(pages-1, cur+2); i++) elems.push(btn(i));
            if (cur<pages-3) elems.push(btn(cur+2, '…', true));
            elems.push(btn(pages));
          }
          return elems;
        })()}
      </Pagination>

      {editUser && (
        <UserEditModal
          initial={editUser}
          groupOptions={groupSuggestions}
          onClose={() => setEditUser(null)}
          onSave={async payload => {
            await updateUser(payload).unwrap();
            if (payload.verified && !editUser.verified) {
              await verifyUser({ id: payload.id }).unwrap();
            }
            setUsers(prev => prev.map(u => u.id===payload.id ? { ...u, ...payload } : u));
            await refetch();
          }}
        />
      )}
    </Wrap>
  );
};
